// API Configuration
// Always use API Gateway (port 3000) as the single entry point
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
export const API_TIMEOUT = 30000; // 30 seconds

// Authentication
export const TOKEN_STORAGE_KEY = 'corp_security_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'corp_security_refresh_token';
export const USER_STORAGE_KEY = 'corp_security_user';
export const TOKEN_EXPIRY_STORAGE_KEY = 'corp_security_token_expiry';

// Inactivity Timeout Configuration
export const LAST_ACTIVITY_STORAGE_KEY = 'corp_security_last_activity';
export const INACTIVITY_TIMEOUT_MS = parseInt(import.meta.env.VITE_INACTIVITY_TIMEOUT_MS ?? '2100000'); // 35 minutes
export const INACTIVITY_WARNING_MS = parseInt(import.meta.env.VITE_INACTIVITY_WARNING_MS ?? '120000'); // 2 minutes before timeout
export const ACTIVITY_DEBOUNCE_MS = 1000; // 1 second debounce
export const ACTIVITY_BROADCAST_CHANNEL = 'corp_security_activity';

// Proactive Token Refresh Configuration
export const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh when <5 min remaining
export const TOKEN_CHECK_INTERVAL_MS = 60 * 1000; // Check every minute

// UI Constants
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;
export const MOBILE_BREAKPOINT = 768;
export const TABLET_BREAKPOINT = 1024;
export const DESKTOP_BREAKPOINT = 1280;

// Theme
export const THEME_STORAGE_KEY = 'corp_security_theme';
export const DEFAULT_THEME = 'system';

// Navigation
export const NAVIGATION_ITEMS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '/security/dashboard',
    icon: 'HomeIcon',
    requiredPermissions: [],
  },
  {
    id: 'incidents',
    label: 'Incidents',
    href: '/security/incidents',
    icon: 'ExclamationTriangleIcon',
    requiredPermissions: ['incidents:read'],
  },
  {
    id: 'executive-protection',
    label: 'Executive Protection',
    href: '/security/executive-protection',
    icon: 'ShieldCheckIcon',
    requiredPermissions: ['executive_protection:read'],
  },
  {
    id: 'threats',
    label: 'Threat Intelligence',
    href: '/security/threats',
    icon: 'EyeIcon',
    requiredPermissions: ['threats:read'],
  },
  {
    id: 'investigations',
    label: 'Investigations',
    href: '/security/investigations',
    icon: 'MagnifyingGlassIcon',
    requiredPermissions: ['investigations:read'],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    href: '/security/analytics',
    icon: 'ChartBarIcon',
    requiredPermissions: ['analytics:read'],
  },
  {
    id: 'settings',
    label: 'Settings',
    href: '/settings',
    icon: 'CogIcon',
    requiredPermissions: [],
  },
] as const;

// Severity levels
export const SEVERITY_LEVELS = {
  LOW: {
    label: 'Low',
    color: 'success',
    value: 1,
  },
  MEDIUM: {
    label: 'Medium',
    color: 'warning',
    value: 2,
  },
  HIGH: {
    label: 'High',
    color: 'danger',
    value: 3,
  },
  CRITICAL: {
    label: 'Critical',
    color: 'danger',
    value: 4,
  },
} as const;

// Status options
export const STATUS_OPTIONS = {
  ACTIVE: {
    label: 'Active',
    color: 'success',
  },
  INACTIVE: {
    label: 'Inactive',
    color: 'secondary',
  },
  PENDING: {
    label: 'Pending',
    color: 'warning',
  },
  ARCHIVED: {
    label: 'Archived',
    color: 'secondary',
  },
} as const;

// Incident statuses
export const INCIDENT_STATUSES = {
  OPEN: {
    label: 'Open',
    color: 'danger',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'warning',
  },
  RESOLVED: {
    label: 'Resolved',
    color: 'success',
  },
  CLOSED: {
    label: 'Closed',
    color: 'secondary',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'secondary',
  },
} as const;

// File upload
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

// Date formats
export const DATE_FORMATS = {
  SHORT: 'MMM d, yyyy',
  LONG: 'MMMM d, yyyy',
  WITH_TIME: 'MMM d, yyyy HH:mm',
  TIME_ONLY: 'HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
} as const;

// Keyboard shortcuts
export const KEYBOARD_SHORTCUTS = {
  SEARCH: 'cmd+k',
  NEW_INCIDENT: 'cmd+n',
  TOGGLE_SIDEBAR: 'cmd+b',
  TOGGLE_THEME: 'cmd+shift+t',
} as const;